import { Autocomplete, TextField } from "@mui/material";
import { useId } from "react";
import { Control, Controller, FieldValues, Path } from "react-hook-form";
import { useUserLdapGroups } from "../auth/useUserLdapGroups.ts";
import { useTranslator } from "../i18n/translator.ts";
import { describedBy } from "./describedBy.ts";
import { InfoToolTip } from "./InfoTooltip.tsx";

export const ProjectPermissionGroupsFormField = <
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
>({
  control,
  name,
  labelText,
  ariaDescribedBy,
}: {
  control: Control<TFieldValues>;
  name: TName;
  labelText: string;
  ariaDescribedBy?: string;
}) => {
  const translator = useTranslator();
  const helpId = useId();
  const userLdapGroups = useUserLdapGroups();

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <Autocomplete
          multiple
          freeSolo
          options={userLdapGroups}
          filterSelectedOptions
          value={field.value ?? []}
          onChange={(_, value) => field.onChange(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              label={
                <span>
                  {labelText}
                  <InfoToolTip
                    helpertext={translator("groupSelectionHelperText")}
                    ariaDescribedById={helpId}
                  ></InfoToolTip>
                </span>
              }
              variant="outlined"
              fullWidth
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
                htmlInput: {
                  ...params.inputProps,
                  "aria-describedby": describedBy(
                    ariaDescribedBy,
                    helpId,
                    !!fieldState.error?.message && `${params.id}-error`,
                  ),
                },
                formHelperText: { id: `${params.id}-error` },
              }}
            />
          )}
        />
      )}
    />
  );
};
